import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { UpsertGoalModal } from '../../components/UpsertGoalModal/UpsertGoalModal';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { FormValues, LargePurchaseData, UpsertDTO } from './LargePurchase.meta';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { useLargePurchase } from './useLargePurchase';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { Label } from '../../../../CommonComponents/OPL/Label/Label';
import { IconButton } from '../../../../CommonComponents/OPL/IconButton/IconButton';
import { useUpsertGoalMutation } from '../../api/useUpsertGoalMutation';
import { AT_RETIREMENT } from '../../Goals.meta';
import { GoalComponentProps } from '../../components/Goal/Goal.meta';
import { AssetBucketingSelect } from '../../components/AssetBucketingSelect/AssetBucketingSelect';

const FORM_ID = 'ProfileGoals--LargePurchase';

interface Props extends GoalComponentProps {
  goalData: LargePurchaseData;
}

export const LargePurchase = ({
  clientId,
  goalData,
  yearsOptions,
  assetBucketingIndices,
  ...rest
}: Props) => {
  const {
    purchaseYearsOptions,
    desiredPurchaseYear,
    purchaseAtRetirement,
    repeatEveryNYears,
    repeatForNYears,
    cost,
    inflation,
  } = useLargePurchase({
    goalData,
    yearsOptions,
  });

  const [isRecurringPurchase, setIsRecurringPurchase] = useState(
    repeatEveryNYears > 1 || repeatForNYears > 1,
  );

  const { register, control, handleSubmit, setValue } = useForm<FormValues>({
    defaultValues: {
      name: rest.name,
      desiredPurchaseYear: purchaseAtRetirement ? AT_RETIREMENT : desiredPurchaseYear,
      purchaseAtRetirement,
      repeatEveryNYears,
      repeatForNYears,
      cost,
      inflation,
      assetBucketingIndices,
    },
  });

  const {
    mutateAsync: upsertGoal,
    isLoading: isUpsertingGoal,
    error: upsertGoalError,
  } = useUpsertGoalMutation({ clientId, url: '/Consumer/GoalUpdate/SaveLargePurchaseGoal' });

  const onSubmit = async (values: FormValues) => {
    try {
      const upsertDTO: UpsertDTO = {
        ...values,
        desiredPurchaseYear: values.purchaseAtRetirement ? null : values.desiredPurchaseYear,
        id: rest.id,
      };

      await upsertGoal(upsertDTO);

      rest.onClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <UpsertGoalModal clientId={clientId} formId={FORM_ID} isLoading={isUpsertingGoal} {...rest}>
      {upsertGoalError && <Alert message={upsertGoalError.message} variant="error" mb="16" />}

      <Box
        id={FORM_ID}
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        display="grid"
        css={`
          grid-template-columns: 1fr;
          gap: 24px;
        `}
        aria-label="Large purchase goal form"
      >
        <Input {...register('name')} label="Purchase Item Name" maxLength={27} />

        <Box>
          <Controller
            control={control}
            name="desiredPurchaseYear"
            render={({ field: { value, onChange } }) => (
              <Select
                label="Desired Purchase Year"
                value={value ? ({ value, label: value } as any) : null}
                options={purchaseYearsOptions}
                onChange={opt => {
                  onChange(opt?.value);
                  setValue('purchaseAtRetirement', opt?.value === AT_RETIREMENT);
                }}
                isPortal
              />
            )}
          />

          {!isRecurringPurchase && (
            <Button variant="link" size="small" onClick={() => setIsRecurringPurchase(true)} mt="4">
              Select Recurring Purchase
            </Button>
          )}
        </Box>

        {isRecurringPurchase && (
          <Box bg="neutral.100" borderRadius="medium" p="16">
            <Label mb="8">Recurring Purchase</Label>

            <Box display="flex" alignItems="center" width="100%" css="gap: 8px">
              <Label>Every</Label>
              <Controller
                control={control}
                name="repeatEveryNYears"
                render={({ field: { onChange, ...field } }) => (
                  <InputNumberMask
                    decimalScale={0}
                    allowNegative={false}
                    onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                    style={{ width: 80 }}
                    {...field}
                  />
                )}
              />
              <Label>year(s) for</Label>
              <Controller
                control={control}
                name="repeatForNYears"
                render={({ field: { onChange, ...field } }) => (
                  <InputNumberMask
                    decimalScale={2}
                    allowNegative={false}
                    onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                    style={{ width: 80 }}
                    {...field}
                  />
                )}
              />
              <Label>year(s)</Label>

              <IconButton
                icon="far fa-times-circle"
                variant="subtleInactive"
                onClick={() => {
                  setValue('repeatForNYears', 1);
                  setValue('repeatEveryNYears', 1);
                  setIsRecurringPurchase(false);
                }}
                aria-label="Remove Recurring Purchase"
                ml="auto"
              />
            </Box>
          </Box>
        )}

        <Controller
          control={control}
          name="cost"
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              label="Cost"
              leftIcon="far fa-dollar-sign"
              decimalScale={2}
              allowNegative={false}
              onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
              {...field}
            />
          )}
        />

        {isRecurringPurchase && (
          <Controller
            control={control}
            name="inflation"
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                label="Inflation"
                rightIcon="far fa-percent"
                decimalScale={2}
                allowNegative={false}
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                {...field}
              />
            )}
          />
        )}

        <AssetBucketingSelect clientId={clientId} control={control} />
      </Box>
    </UpsertGoalModal>
  );
};
